import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

// Check if running inside Capacitor native app
export const isNativePlatform = () => Capacitor.isNativePlatform();

// Request speech permission (native only)
export const requestSpeechPermission = async (): Promise<boolean> => {
  if (!isNativePlatform()) return true; // Browser handles its own permissions
  try {
    const status = await SpeechRecognition.requestPermissions();
    return status?.speechRecognition === 'granted';
  } catch (e) {
    console.error('Speech permission error:', e);
    return false;
  }
};

// Check if speech recognition is available
export const isSpeechAvailable = async (): Promise<boolean> => {
  if (!isNativePlatform()) {
    return !!(window as any).SpeechRecognition || !!(window as any).webkitSpeechRecognition;
  }
  try {
    const result = await SpeechRecognition.available();
    return result?.available ?? false;
  } catch {
    return false;
  }
};

interface NativeSpeechCallbacks {
  onResult: (text: string, isFinal: boolean) => void;
  onStart: () => void;
  onEnd: () => void;
  onError: (error: string) => void;
}

let isNativeListening = false;
let nativeListenLoop: any = null;

// Start native speech recognition with continuous listening
export const startNativeSpeech = async (
  lang: string,
  callbacks: NativeSpeechCallbacks
): Promise<boolean> => {
  if (!isNativePlatform()) return false;

  const permitted = await requestSpeechPermission();
  if (!permitted) {
    callbacks.onError('not-allowed');
    return false;
  }

  isNativeListening = true;
  callbacks.onStart();

  // Native speech recognition loop (restart after each result for continuous listening)
  const listen = async () => {
    if (!isNativeListening) return;
    try {
      const result = await SpeechRecognition.start({
        language: lang,
        maxResults: 3,
        partialResults: true,
        popup: false,
      });

      if (result?.matches && result.matches.length > 0) {
        const text = result.matches[0];
        if (text) {
          callbacks.onResult(text, true);
        }
      }

      // Continue listening if still active
      if (isNativeListening) {
        nativeListenLoop = setTimeout(listen, 200);
      }
    } catch (e: any) {
      if (isNativeListening) {
        if (e?.message?.includes('not-allowed') || e?.code === 'not-allowed') {
          callbacks.onError('not-allowed');
          isNativeListening = false;
        } else {
          // Restart on transient errors (no-speech, network, etc.)
          nativeListenLoop = setTimeout(listen, 500);
        }
      }
    }
  };

  listen();
  return true;
};

// Stop native speech recognition
export const stopNativeSpeech = async () => {
  isNativeListening = false;
  if (nativeListenLoop) {
    clearTimeout(nativeListenLoop);
    nativeListenLoop = null;
  }
  try {
    await SpeechRecognition.stop();
  } catch (e) {
    // Ignore stop errors
  }
};
